'use client'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { userLogin, registerUser } from '../repositories/authRepository'
import { setCookie, clearCookie, clearData, setData } from '../lib'
import { AUTH_TOKEN_KEY, USER_DATA_KEY } from '../utils/constDictionary'
import getNumberWithCountryCode from '../lib/getNumberWithCountryCode'
import axios from 'axios'

export const login = createAsyncThunk(
  'auth/login',
  async (userData, { rejectWithValue }) => {
    const data = {
      username: getNumberWithCountryCode(userData.username),
      password: userData.password,
      remember: Boolean(userData.remember),
    }
    if (userData?.['g-recaptcha-response']) {
      data['g-recaptcha-response'] = userData?.['g-recaptcha-response']
    }
    if (userData.authProvider) {
      data.authProvider = userData.authProvider
    }

    try {
      const response = await userLogin({ ...data })
      setData(USER_DATA_KEY, JSON.stringify(response))
      await setCookie(AUTH_TOKEN_KEY, response.accessToken)
      axios.defaults.headers.common.Authorization = `Bearer ${response.accessToken}`
      return { customer: response }
    } catch (error) {
      return rejectWithValue(error?.response?.data || {})
    }
  }
)

export const signup = createAsyncThunk(
  'auth/signup',
  async ({ newSignupData, isCustomerGroupsEnabled }, { rejectWithValue }) => {
    const { businessName, username, emailOrPhone, phone, ...rest } =
      newSignupData
    const emailAvailable = typeof emailOrPhone !== 'object'
    const data = {
      ...(!isCustomerGroupsEnabled && { name: username.trim() }),
      ...(isCustomerGroupsEnabled && { name: businessName }),
      ...(emailAvailable && { email: emailOrPhone }),
      ...(phone && { phone: getNumberWithCountryCode(phone) }),
      ...rest,
    }

    try {
      const response = await registerUser({ ...data })
      return response
    } catch (error) {
      return rejectWithValue(error?.response?.data || {})
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    userData: {},
    isLoggedin: false,
    showLoginForm: false,
    loading: false,
    error: null,
  },
  reducers: {
    logout: (state) => {
      state.userData = {}
      state.isLoggedin = false
      clearData(USER_DATA_KEY)
      clearCookie(AUTH_TOKEN_KEY)
      axios.defaults.headers.common.Authorization = ''
    },
    toggleLoginForm: (state, action) => {
      state.showLoginForm = action.payload
    },
    updateUserData: (state, action) => {
      state.userData = { ...state.userData, ...action.payload }
      if (action.payload?.accessToken) {
        state.isLoggedin = true
        axios.defaults.headers.common.Authorization = `Bearer ${action.payload?.accessToken}`
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false
        state.userData = action.payload.customer
        state.isLoggedin = true
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(signup.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(signup.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(signup.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { logout, toggleLoginForm, updateUserData } = authSlice.actions
export default authSlice.reducer
